import type { AIFeedbackResponse } from "@memorang/types";
import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import type { TrackAnswerEventRequest } from "../api/types";

type ResponseData = Record<string, unknown>;
type AIResponseMap = Record<string, AIFeedbackResponse>;

type AIFeedbackState = {
	streaming: boolean;
	userResponse?: ResponseData;
	canvasResponse?: ResponseData;
	audioResponse?: ResponseData;
	// Recording length (ms) per item. Kept out of audioResponse, which gates
	// feedback generation and submit state.
	recordingDurations: Record<number, number>;
	aiResponse?: AIResponseMap;
	answerEventRequests: Record<number, TrackAnswerEventRequest>;
	showError: boolean;
	slideshowDurationInSecondsByItemId: Record<number, number>;
	setStreaming: (streaming: boolean) => void;
	setUserResponse: (itemId: number, data: unknown) => void;
	setCanvasResponse: (itemId: number, data: unknown) => void;
	setAudioResponse: (itemId: number, data: unknown) => void;
	setRecordingDuration: (itemId: number, durationMillis: number) => void;
	setShowError: (showError: boolean) => void;
	stashAnswerEventRequest: (
		itemId: number,
		request: TrackAnswerEventRequest,
	) => void;
	getStashedAnswerEventRequest: (
		itemId: number,
	) => TrackAnswerEventRequest | undefined;
	clearStashedAnswerEventRequest: (itemId: number) => void;
	updateTranscription: (itemId: number, data: string) => void;
	resetAiFeedbackStore: () => void;
	updateRunDetails: (
		itemId: number,
		data: AIFeedbackResponse["runDetails"],
	) => void;
	updateSubskillAssessments: (
		itemId: number,
		data: AIFeedbackResponse["subskillAssessments"],
	) => void;
	updateScore: (itemId: number, data: AIFeedbackResponse["score"]) => void;
	updateDisplayScore: (
		itemId: number,
		data: AIFeedbackResponse["displayScore"],
	) => void;
	updateNormalizedPoints: (
		itemId: number,
		data: AIFeedbackResponse["normalizedPoints"],
	) => void;
	updateExplanation: (
		itemId: number,
		data: AIFeedbackResponse["explanation"],
	) => void;
	updateAnswer: (itemId: number, data: AIFeedbackResponse["answer"]) => void;
	updateStatus: (itemId: number, data: AIFeedbackResponse["status"]) => void;
	incrementRetries: (itemId: number) => void;
	updateSlideshowDurationInSeconds: (itemId: number, duration: number) => void;
};
const useAIFeedbackStore = create<AIFeedbackState>()(
	immer((set, get) => ({
		streaming: false,
		showError: false,
		slideshowDurationInSecondsByItemId: {},
		recordingDurations: {},
		answerEventRequests: {},
		setStreaming: (streaming) =>
			set((state) => {
				state.streaming = streaming;
			}),
		stashAnswerEventRequest: (itemId, request) =>
			set((state) => {
				state.answerEventRequests[itemId] = request;
			}),
		getStashedAnswerEventRequest: (itemId) => get().answerEventRequests[itemId],
		clearStashedAnswerEventRequest: (itemId) =>
			set((state) => {
				delete state.answerEventRequests[itemId];
			}),
		setUserResponse: (itemId, data) =>
			set((state) => {
				if (!state.userResponse) {
					state.userResponse = {};
				}
				if (data === undefined) {
					delete state.userResponse[itemId];
					return;
				}
				state.userResponse[itemId] = data;
			}),
		setCanvasResponse: (itemId, data) =>
			set((state) => {
				if (!state.canvasResponse) {
					state.canvasResponse = {};
				}
				state.canvasResponse[itemId] = data;
			}),
		setAudioResponse: (itemId, data) =>
			set((state) => {
				if (!state.audioResponse) {
					state.audioResponse = {};
				}
				state.audioResponse[itemId] = data;
			}),
		setRecordingDuration: (itemId, durationMillis) =>
			set((state) => {
				state.recordingDurations[itemId] = durationMillis;
			}),
		setShowError: (showError) =>
			set((state) => {
				state.showError = showError;
			}),
		updateTranscription: (itemId, transcription) =>
			set((state) => {
				state.aiResponse = state.aiResponse || {};
				state.aiResponse[itemId] = state.aiResponse[itemId] || {};
				state.aiResponse[itemId].transcription = transcription;
			}),
		resetAiFeedbackStore: () =>
			set((state) => {
				state.streaming = false;
				state.showError = false;
				state.userResponse = undefined;
				state.canvasResponse = undefined;
				state.audioResponse = undefined;
				state.recordingDurations = {};
				state.aiResponse = undefined;
				state.answerEventRequests = {};
			}),
		updateRunDetails: (itemId, data) =>
			set((state) => {
				state.aiResponse = state.aiResponse || {};
				state.aiResponse[itemId] = state.aiResponse[itemId] || {};
				state.aiResponse[itemId].runDetails = data;
			}),
		updateSubskillAssessments: (itemId, data) =>
			set((state) => {
				state.aiResponse = state.aiResponse || {};
				state.aiResponse[itemId] = state.aiResponse[itemId] || {};
				state.aiResponse[itemId].subskillAssessments = data;
			}),
		updateScore: (itemId, data) =>
			set((state) => {
				state.aiResponse = state.aiResponse || {};
				state.aiResponse[itemId] = state.aiResponse[itemId] || {};
				state.aiResponse[itemId].score = data;
			}),
		updateDisplayScore: (itemId, data) =>
			set((state) => {
				state.aiResponse = state.aiResponse || {};
				state.aiResponse[itemId] = state.aiResponse[itemId] || {};
				state.aiResponse[itemId].displayScore = data;
			}),
		updateNormalizedPoints: (itemId, data) =>
			set((state) => {
				state.aiResponse = state.aiResponse || {};
				state.aiResponse[itemId] = state.aiResponse[itemId] || {};
				state.aiResponse[itemId].normalizedPoints = data;
			}),
		updateExplanation: (itemId, data) =>
			set((state) => {
				state.aiResponse = state.aiResponse || {};
				state.aiResponse[itemId] = state.aiResponse[itemId] || {};
				state.aiResponse[itemId].explanation = data;
			}),
		updateAnswer: (itemId, data) =>
			set((state) => {
				state.aiResponse = state.aiResponse || {};
				state.aiResponse[itemId] = state.aiResponse[itemId] || {};
				state.aiResponse[itemId].answer = data;
			}),
		updateStatus: (itemId, data) =>
			set((state) => {
				state.aiResponse = state.aiResponse || {};
				state.aiResponse[itemId] = state.aiResponse[itemId] || {};
				state.aiResponse[itemId].status = data;
			}),
		incrementRetries: (itemId) =>
			set((state) => {
				state.aiResponse = state.aiResponse || {};
				state.aiResponse[itemId] = state.aiResponse[itemId] || {};
				state.aiResponse[itemId].retries =
					(state.aiResponse[itemId].retries || 0) + 1;
			}),
		updateSlideshowDurationInSeconds: (itemId, duration) =>
			set((state) => {
				state.slideshowDurationInSecondsByItemId[itemId] = duration;
			}),
	})),
);

export default useAIFeedbackStore;
